import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { ERoleType } from '@/features/user-management/constants'
import { RoleWithScopes, User } from '@/schema'
import { ApiError } from '@/schema/common'

import { authApi } from './authApi'
/**
 * Type guard to check if error is an ApiError
 */
const isApiError = (error: unknown): error is ApiError => {
  return typeof error === 'object' && error !== null && 'response' in error
}
// Auth store state and actions
interface AuthStore {
  // State
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  roles?: RoleWithScopes[]

  // Actions
  getCurrentUser: () => Promise<void>
  clearError: () => void
  setToken: (token: string | null) => void
  setRoles: (user: User) => void
  getRoles: () => ERoleType[] | []
  getRolesWithScopes: () => RoleWithScopes[] | []
  getListDLViewerHasAccess: () => string[] | []
  getListDLReviewerHasAccess: () => string[] | []
  getListCompanyUserHasAccess: () => string[]
  getCompaniesFromRoles: () => Array<{ id: string; name: string }>
  getListProjectUserHasAccess: () => string[]
  getListDeliverableSetUserHasAccess: () => string[]
  getListProjectTeamMemberHasAccess: () => string[]
  getListPrjProjectLeadHasAccess: () => string[]
  reset: () => void
}

// Initial state
const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  isLoading: false,
  error: null,
  roles: [] as RoleWithScopes[],
}

/**
 * Auth Store
 *
 * Manages authentication state using Zustand.
 * Replaces Redux Toolkit authSlice.
 *
 * Features:
 * - User authentication (login/logout)
 * - Token management with localStorage
 * - Error handling
 * - Redux DevTools integration
 *
 */

export const useAuthStore = create<AuthStore>()(
  devtools(
    (set, get) => ({
      ...initialState,
      getCurrentUser: async () => {
        const token = get().token

        if (!token) {
          set({
            user: null,
            token: null,
            roles: [],
            error: 'No token available',
            isAuthenticated: false,
          })
          return
        }

        set({ isLoading: true, error: null })

        try {
          const user = await authApi.getCurrentUser()

          set({
            user,
            roles: user.roles || [],
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } catch (error: unknown) {
          const errorMessage = isApiError(error)
            ? error.response?.data?.message || 'Failed to get user'
            : 'Failed to get user'

          // Remove invalid token
          localStorage.removeItem('token')

          set({
            user: null,
            token: null,
            roles: [],
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          })
          throw error
        }
      },

      /**
       * Clear error message
       */
      clearError: () => {
        set({ error: null })
      },

      /**
       * Set authentication token
       */
      setToken: (token: string | null) => {
        if (token) {
          localStorage.setItem('token', token)
        } else {
          localStorage.removeItem('token')
        }
        set({ token })
      },

      setRoles: (user: User) => {
        const roles = user.roles || []
        set({ roles })
      },

      getRoles: () => {
        const roles = get().roles || []
        return roles.map((role) => role.role_name)
      },
      getRolesWithScopes: () => {
        return get().roles || []
      },
      getListProjectTeamMemberHasAccess: () => {
        const roles = get().roles || []
        const teamMemberRole = roles.find(
          (role) => role.role_name === ERoleType.TEAM_MEMBER
        )?.scopes
        const listProjectMemberHasAccess = teamMemberRole
          ? teamMemberRole
              .map((scope) => scope.project_id)
              .filter((id): id is string => !!id)
          : []
        return listProjectMemberHasAccess
      },
      getListPrjProjectLeadHasAccess: () => {
        const roles = get().roles || []
        const projectLeadRole = roles.find(
          (role) => role.role_name === ERoleType.PROJECT_LEAD
        )?.scopes
        const listProjectLeadHasAccess = projectLeadRole
          ? projectLeadRole
              .map((scope) => scope.project_id)
              .filter((id): id is string => !!id)
          : []
        return listProjectLeadHasAccess
      },
      getListDLViewerHasAccess: () => {
        const roles = get().roles || []
        const viewerRole = roles.find(
          (role) => role.role_name === ERoleType.VIEWER
        )?.scopes
        const listDLViewerHasAccess = viewerRole
          ? viewerRole
              .map((scope) => scope.deliverable_set_id)
              .filter((id): id is string => !!id)
          : []
        return listDLViewerHasAccess
      },
      getListDLReviewerHasAccess: () => {
        const roles = get().roles || []
        const reviewerRole = roles.find(
          (role) => role.role_name === ERoleType.REVIEWER
        )?.scopes
        const listDLReviewerHasAccess = reviewerRole
          ? reviewerRole
              .map((scope) => scope.deliverable_set_id)
              .filter((id): id is string => !!id)
          : []
        return listDLReviewerHasAccess
      },
      getListCompanyUserHasAccess: () => {
        const roles = get().roles || []
        const allScopes = roles.flatMap((role) => role.scopes || [])
        const companyIds = allScopes
          .map((scope) => scope.company_id)
          .filter((id): id is string => !!id)
        return Array.from(new Set(companyIds))
      },
      getCompaniesFromRoles: () => {
        const roles = get().roles || []
        const allScopes = roles.flatMap((role) => role.scopes || [])
        const companiesMap = new Map<string, string>()

        allScopes.forEach((scope) => {
          if (scope.company_id && scope.company_name) {
            companiesMap.set(scope.company_id, scope.company_name)
          }
        })

        return Array.from(companiesMap.entries())
          .map(([id, name]) => ({ id, name }))
          .sort((a, b) => a.name.localeCompare(b.name))
      },
      getListProjectUserHasAccess: () => {
        const roles = get().roles || []
        const allScopes = roles.flatMap((role) => role.scopes || [])
        const projectIds = allScopes
          .map((scope) => scope.project_id)
          .filter((id): id is string => !!id)
        return Array.from(new Set(projectIds))
      },
      getListDeliverableSetUserHasAccess: () => {
        const roles = get().roles || []
        const allScopes = roles.flatMap((role) => role.scopes || [])
        const deliverableSetIds = allScopes
          .map((scope) => scope.deliverable_set_id)
          .filter((id): id is string => !!id)
        return Array.from(new Set(deliverableSetIds))
      },

      reset: () => {
        localStorage.removeItem('token')

        set({
          ...initialState,
          user: null,
          token: null,
          roles: [],
          isAuthenticated: false,
          error: null,
          isLoading: false,
        })
      },
    }),
    {
      name: 'AuthStore',
    }
  )
)

/**
 * Selectors for common use cases
 */
export const authSelectors = {
  isAuthenticated: (state: AuthStore) => state.isAuthenticated,
  user: (state: AuthStore) => state.user,
  isLoading: (state: AuthStore) => state.isLoading,
  error: (state: AuthStore) => state.error,
  token: (state: AuthStore) => state.token,
  roles: (state: AuthStore) => state.roles,
}
